import type { SiteDetail, SiteStep } from '@tabel/shared/schemas/sites';
import { cityLabel } from '@tabel/shared/sites/cities';
import {
  COMMON,
  type StepView,
  localDate,
  siteTextContext,
  stepReady,
  stepView,
} from '@tabel/shared/sites/step-texts';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, Clock3, Copy, MapPinOff } from 'lucide-react';
import { type CSSProperties, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button, Card, MetaChip, PageHeader, Skeleton, type StepItem, Steps } from '../../../components/ui';
import { useSetCrumbs, useSetPageFreshness } from '../../../lib/crumbs';
import { reportClientError } from '../../../lib/errors';
import { SETTINGS_PATH } from '../../../lib/nav';
import { useRole } from '../../../lib/role';
import { siteDetail, sitesKeys } from '../api';
import { SITES_CRUMB, settingsCrumbs } from '../crumbs';
import { LoadError, Refused, SCREEN, isForbidden, isNotFound, shouldReport } from '../parts';
import { SITE } from '../texts';
import { DangerDialogs, DangerZone } from './DangerZone';
import { Repair, showsRepair } from './Repair';
import { SiteAlerts } from './SiteAlerts';
import { SiteCtxProvider, useSiteActions } from './actions';
import { Step1, Step2, Step3, Step4, Step5, Step6, Step7, Step8, Step9, Step10 } from './bodies';
import { CopyDialog, RiskDialog } from './dialogs';
import { STEP_STATE, currentStep, stepAnchor, stepFromHash } from './model';

/*
 * Страница площадки (007 T040, T041, T047, T053, T055; spec.md FR-010…FR-027, FR-041, FR-050, FR-051, FR-061, FR-062,
 * US1, US3, US4, US6, US7; research R12, R19; прототип prototypes/site-onboarding.html, `viewSite`;
 * site-onboarding-design.md разделы 2–4, 6). Раздел-форма до 720 px, крошки «Настройки / Площадки / <название>».
 * Сверху — название, пилюли «Часовой пояс» и «Начато», «Готово N из 10» и «Сведения для поддержки». Под ними — «Что случилось»
 * (открытые тревоги), если они есть. Ниже — 10 шагов `Steps`: открыт текущий шаг (или шаг из адреса `#step6` — ссылка
 * для телефона), остальные свёрнуты до заголовка и строки состояния. Потом — «Ремонт» (у наблюдаемой и работающей
 * площадки), внизу — «Опасная зона».
 *
 * Состояние шагов считает сервер (`GET /api/sites/:site`, research R19); страница перечитывает его раз в 5 с, пока
 * открыта (S101), и после каждого действия. Тексты — только `@tabel/shared/sites/step-texts` (`stepView`).
 */

/** Опрос страницы площадки (research R12, S101): раз в 5 с и только открытой страницы. */
export const POLL_MS = 5000;

export function SiteSection() {
  const { site: code = '' } = useParams();
  const { role } = useRole();
  const location = useLocation();
  const q = useQuery({
    queryKey: sitesKeys.site(role, code),
    queryFn: () => siteDetail(code),
    // Ошибка — опрос стоит: повтор — кнопкой «Повторить» (403, 404 повтор не исправит).
    refetchInterval: (query) => (query.state.status === 'error' ? false : POLL_MS),
  });
  const d = q.data;
  useSetCrumbs(settingsCrumbs(SITES_CRUMB, { label: d?.site.name ?? code }));
  // Плашка «Данные СКУД» в шапке — последняя отметка этой площадки, а не объекта пары (FR-053).
  useSetPageFreshness(
    d ? { site: d.site.name, tzOffset: d.site.tzOffset, lastPassAt: d.lastPassAt } : 'pending',
  );

  useEffect(() => {
    if (q.error && shouldReport(q.error)) reportClientError(SCREEN, 'site', q.error);
  }, [q.error]);

  // Открытые шаги. При первом ответе — текущий шаг и шаг из адреса; дальше раскрывает человек и ссылки подсказок.
  const [open, setOpen] = useState<ReadonlySet<number>>(new Set());
  const started = useRef(false);
  const scrollTo = useCallback((n: number) => {
    requestAnimationFrame(() =>
      document.getElementById(stepAnchor(n))?.scrollIntoView?.({ block: 'start', behavior: 'smooth' }),
    );
  }, []);
  const openStep = useCallback(
    (n: number) => {
      setOpen((s) => new Set(s).add(n));
      scrollTo(n);
    },
    [scrollTo],
  );
  useEffect(() => {
    if (!d || started.current) return;
    started.current = true;
    const fromHash = stepFromHash(location.hash);
    const now = currentStep(d.steps);
    setOpen(new Set([now, fromHash].filter((n): n is number => n !== null)));
    if (fromHash) scrollTo(fromHash);
  }, [d, location.hash, scrollTo]);

  const { act, busy, dialog, setDialog, password } = useSiteActions(code, d, openStep);

  let content: ReactNode;
  if (q.isError) {
    if (isForbidden(q.error))
      content = <Refused title={SITE.forbiddenTitle} text={SITE.forbidden} error={q.error} />;
    else if (isNotFound(q.error))
      content = (
        <Refused
          icon={MapPinOff}
          title={SITE.notFoundTitle}
          text={SITE.notFound}
          error={q.error}
          action={
            <Link to={SETTINGS_PATH.sites} className="text-[13px] font-medium text-accent hover:underline">
              {SITE.toList}
            </Link>
          }
        />
      );
    else
      content = (
        <LoadError
          title={SITE.loadError}
          error={q.error}
          busy={q.isFetching}
          onRetry={() => void q.refetch()}
        />
      );
  } else if (!d) content = <Loading />;

  const toggle = (key: string | number) =>
    setOpen((s) => {
      const next = new Set(s);
      const n = Number(key);
      if (!next.delete(n)) next.add(n);
      return next;
    });

  return (
    <>
      <PageHeader
        title={d?.site.name ?? code}
        description={SITE.description}
        meta={d ? <SiteMeta d={d} /> : undefined}
        actions={
          d ? (
            <Button icon={Copy} disabled={busy !== null} onClick={() => void act.support()}>
              {COMMON.support}
            </Button>
          ) : undefined
        }
      />
      {content ? (
        <div className="mt-6">{content}</div>
      ) : (
        d && (
          <SiteCtxProvider value={{ detail: d, act, busy, password, setDialog }}>
            <Progress d={d} />
            <SiteAlerts />
            <div className="mt-6">
              <Steps label={SITE.stepsLabel} items={stepItems(d)} open={open} onToggle={toggle} />
            </div>
            {showsRepair(d) && <Repair />}
            <DangerZone />
            <DangerDialogs dialog={dialog} />
            <RiskDialog step={dialog?.kind === 'risk' ? dialog.step : null} onClose={() => setDialog(null)} />
            <CopyDialog copy={dialog?.kind === 'copy' ? dialog : null} onClose={() => setDialog(null)} />
          </SiteCtxProvider>
        )
      )}
    </>
  );
}

const BODIES: Readonly<Record<number, (p: { step: SiteStep; view: StepView }) => ReactNode>> = {
  1: Step1,
  2: Step2,
  3: Step3,
  4: Step4,
  5: Step5,
  6: Step6,
  7: Step7,
  8: Step8,
  9: Step9,
  10: Step10,
};

/** Шаги для `Steps`: заголовок, кружок по статусу, строка состояния и тело — по ответу сервера и `stepView`. */
function stepItems(d: SiteDetail): StepItem[] {
  const ctx = siteTextContext(d);
  return d.steps.map((s) => {
    const view = stepView(s, ctx);
    const Body = BODIES[s.step];
    return {
      key: s.step,
      title: (
        <span id={stepAnchor(s.step)} className="scroll-mt-20">
          {view.heading}
        </span>
      ),
      state: STEP_STATE[s.status],
      status: view.summary,
      badge: view.optional ? COMMON.optional : undefined,
      body: Body ? <Body step={s} view={view} /> : null,
    };
  });
}

/** Пилюли под заголовком: город с поясом и дата начала подключения (дд.мм.гггг по поясу площадки). */
function SiteMeta({ d }: { d: SiteDetail }) {
  return (
    <>
      <MetaChip icon={Clock3} label={SITE.cityChip}>
        {cityLabel(d.site.city, d.site.tzOffset)}
      </MetaChip>
      <MetaChip icon={CalendarDays} label={SITE.startedChip}>
        {localDate(d.site.createdAt, d.site.tzOffset)}
      </MetaChip>
    </>
  );
}

/** «Готово N из 10» — полоса `t-prog` (дизайн-система Progress), число справа. Готов — `ok` или риск принят. */
function Progress({ d }: { d: SiteDetail }) {
  const done = d.steps.filter(stepReady).length;
  return (
    <div className="mt-4 flex items-center gap-3 text-[13px] text-ink-2">
      {/* Полоса — для глаза; число шагов говорит подпись рядом. */}
      <span
        aria-hidden
        className={`t-prog max-w-[260px] ${done === 10 ? 'ok' : ''}`}
        style={{ '--p': String(done / 10) } as CSSProperties}
      >
        <i />
      </span>
      <span className="tabular-nums">{COMMON.progress(done)}</span>
    </div>
  );
}

function Loading() {
  return (
    <Card className="mt-6 space-y-3 p-4" aria-busy="true" aria-label={SITE.loading}>
      <Skeleton className="h-5 w-1/2" />
      <Skeleton className="h-5 w-2/3" />
      <Skeleton className="h-5 w-1/3" />
    </Card>
  );
}
